import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Edit, Trash2, ExternalLink, BarChart3, Copy } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/components/app/AdminLayout";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

export default function AdminCampaigns() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/campaigns");
      setList(data);
    } catch (e) {
      toast.error("שגיאה בטעינה");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("למחוק את הקמפיין?")) return;
    try {
      await api.delete(`/campaigns/${id}`);
      toast.success("הקמפיין נמחק");
      load();
    } catch (_) {
      toast.error("שגיאה במחיקה");
    }
  };

  const copyLink = (slug) => {
    const url = `${window.location.origin}/c/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success("הקישור הועתק");
  };

  return (
    <AdminLayout title="קמפיינים">
      <div className="flex justify-between mb-6">
        <p className="text-stone-600 text-sm">סך הכל {list.length} קמפיינים.</p>
        <Link to="/admin/campaigns/new" data-testid="new-campaign-btn">
          <Button className="bg-brand-amber hover:bg-brand-amberHover rounded-full h-11 font-bold">
            <Plus className="w-4 h-4 me-2" />
            קמפיין חדש
          </Button>
        </Link>
      </div>

      {loading ? (
        <p>טוען...</p>
      ) : list.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-stone-200">
          <p className="text-stone-500 mb-4">אין קמפיינים עדיין.</p>
          <Link to="/admin/campaigns/new">
            <Button className="bg-brand-amber hover:bg-brand-amberHover rounded-full h-11">צרו קמפיין ראשון</Button>
          </Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="campaigns-grid">
          {list.map((c) => (
            <div key={c.id} className="bg-white rounded-2xl p-5 border border-stone-200 hover:shadow-lg transition" data-testid={`campaign-${c.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-[800] text-lg truncate">{c.brand_name}</h3>
                  <p className="text-xs text-stone-500 truncate">/{c.slug}</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${c.is_active ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-600"}`}>
                    {c.is_active ? "פעיל" : "כבוי"}
                  </span>
                  {c.is_default && (
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-brand-amber">ברירת מחדל</span>
                  )}
                </div>
              </div>
              <div className="text-xs space-y-1 text-stone-500 mb-4">
                <div><strong>List:</strong> {c.list_id}</div>
                <div className="truncate"><strong>Affiliate:</strong> {c.affiliate_url}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Link to={`/admin/campaigns/${c.id}`} className="flex-1">
                  <Button variant="outline" className="w-full rounded-full h-9 text-xs" data-testid={`edit-${c.id}`}>
                    <Edit className="w-3 h-3 me-1" /> עריכה
                  </Button>
                </Link>
                <Link to={`/admin/analytics/${c.id}`}>
                  <Button variant="outline" className="rounded-full h-9 px-3 text-xs">
                    <BarChart3 className="w-3 h-3" />
                  </Button>
                </Link>
                <Button variant="outline" className="rounded-full h-9 px-3 text-xs" onClick={() => copyLink(c.slug)}>
                  <Copy className="w-3 h-3" />
                </Button>
                <a href={`/c/${c.slug}`} target="_blank" rel="noreferrer">
                  <Button variant="outline" className="rounded-full h-9 px-3 text-xs">
                    <ExternalLink className="w-3 h-3" />
                  </Button>
                </a>
                <Button
                  variant="outline"
                  onClick={() => remove(c.id)}
                  data-testid={`delete-${c.id}`}
                  className="rounded-full h-9 px-3 text-xs text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
